import type { UseFormReturn } from 'react-hook-form';
import type { BookingFormData } from '@geely/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@geely/ui';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { MaskedPlateInput } from '@/app/components/booking/MaskedPlateInput';
import { MaskedVinInput } from '@/app/components/booking/MaskedVinInput';
import { MaskedMileageInput } from '@/app/components/booking/MaskedMileageInput';
import { useActiveModels } from '@/hooks/useModels';

interface VehicleInfoStepProps {
  form: UseFormReturn<BookingFormData>;
  onBack: () => void;
  onNext: () => void;
}

export function VehicleInfoStep({ form, onBack, onNext }: VehicleInfoStepProps) {
  const { data: models = [] } = useActiveModels();
  const selectedModelId = form.watch('modelId');
  const selectedModel = models.find((m) => m.id === selectedModelId);
  const versions = selectedModel?.versions || [];

  const handleModelChange = (modelId: string) => {
    form.setValue('modelId', modelId);
    form.setValue('versionId', '');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{'Dados do Ve\u00edculo'}</CardTitle>
        <CardDescription>{'Informe os dados do seu ve\u00edculo'}</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="vehiclePlate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Placa *</FormLabel>
                  <FormControl>
                    <MaskedPlateInput
                      value={field.value}
                      onChange={field.onChange}
                      name={field.name}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="vehicleVin"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Chassi (VIN)</FormLabel>
                  <FormControl>
                    <MaskedVinInput
                      value={field.value || ''}
                      onChange={(v) => field.onChange(v || undefined)}
                      name={field.name}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="vehicleMileage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quilometragem *</FormLabel>
                  <FormControl>
                    <MaskedMileageInput
                      value={field.value}
                      onChange={field.onChange}
                      name={field.name}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="modelId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Modelo *</FormLabel>
                  <Select onValueChange={handleModelChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o modelo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {models.map((model) => (
                        <SelectItem key={model.id} value={model.id}>
                          {model.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="versionId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{'Vers\u00e3o *'}</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={!selectedModelId}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={selectedModelId ? 'Selecione a vers\u00e3o' : 'Selecione um modelo primeiro'} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {versions.map((version) => (
                        <SelectItem key={version.id} value={version.id}>
                          {version.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={onBack}>
                <ArrowLeft className="size-4 mr-2" />
                Voltar
              </Button>
              <Button onClick={onNext}>
                {'Pr\u00f3ximo'}
                <ArrowRight className="size-4 ml-2" />
              </Button>
            </div>
          </div>
        </Form>
      </CardContent>
    </Card>
  );
}
